import * as z from 'zod';

import {
  BankruptcyId,
  BankruptcyProofOfClaimId,
  FeeId,
  PaymentDueId,
  PaymentId,
  ReportableEventId,
} from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { AuthorizedContactPaymentPayer, BorrowerPaymentPayer, EntityPaymentPayer } from './Payments';

export const BankruptcyChapter = z.enum(['CHAPTER_7', 'CHAPTER_11', 'CHAPTER_12', 'CHAPTER_13']);
export type BankruptcyChapter = z.infer<typeof BankruptcyChapter>;

export const BankruptcyStatus = z.enum(['active', 'closed', 'archived']);
export type BankruptcyStatus = z.infer<typeof BankruptcyStatus>;

export const BankruptcyOutcome = z.enum(['dismissed', 'discharged', 'converted', 'other']);
export type BankruptcyOutcome = z.infer<typeof BankruptcyOutcome>;

export const BankruptcyFiledBy = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
]);
export type BankruptcyFiledBy = z.infer<typeof BankruptcyFiledBy>;

/**
 * A frozen-in-time picture of the loan as of the petition filing date, per the
 * official Form 410A instructions. Post-petition state is never read from here —
 * the workout plan recalculates its own arrearage split when it is created.
 */
export const BankruptcyProofOfClaim = z.object({
  id: BankruptcyProofOfClaimId,
  createdAt: zodJSInstantDate,
  petitionFilingDate: zodJSPlainDate,

  // paymentsDue due before petitionFilingDate that were outstanding as of the
  // petition date (unpaid now, or fulfilled on/after the filing date).
  paymentDueIdsPrePetition: z.array(PaymentDueId),

  // unfulfilled charges with a charge date strictly before the petition date
  chargeIdsPrePetition: z.array(FeeId),

  /**
   * Forward-looking RESPA escrow-analysis shortage, frozen on the claim. The
   * petition-date balance figures (principal, escrow, suspense) are recomputed
   * live from the ledger at render time rather than stored, so this is the only
   * dollar amount the claim itself persists.
   */
  projectedEscrowShortage: z.number(),

  /** date of first default (user input, prefilled with willow's date of first delinquency) */
  dofd: zodJSPlainDate,
  /** payments from the dofd through the filing date; powers the Part 5 history */
  paymentIdsSinceDofd: z.array(PaymentId),
});
export type BankruptcyProofOfClaim = z.infer<typeof BankruptcyProofOfClaim>;

export const Bankruptcy = z.object({
  id: BankruptcyId,
  chapter: BankruptcyChapter,
  filedAt: zodJSPlainDate,
  filedBy: BankruptcyFiledBy.array().min(1, { message: 'At least one filer required' }),

  /** the bankruptcy court case number, when known (used on the proof of claim) */
  caseNumber: z.string().optional(),

  status: BankruptcyStatus,
  statusHistory: z
    .object({
      status: BankruptcyStatus,
      changedAt: zodJSInstantDate,
    })
    .array(),
  archivedAt: zodJSInstantDate.optional(),

  /** date bankruptcy exited active status */
  endDate: zodJSPlainDate.optional(),

  /** outcome of a cancelled or archived bankruptcy */
  endOutcome: BankruptcyOutcome.optional(),

  reportableEventIds: z.array(ReportableEventId),

  /** appended on each filing/amendment; the latest entry is authoritative */
  proofOfClaim: z.array(BankruptcyProofOfClaim),

  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
});
export type Bankruptcy = z.infer<typeof Bankruptcy>;

export const BankruptcyInput = Bankruptcy.pick({
  id: true,
  chapter: true,
  filedAt: true,
  filedBy: true,
  endDate: true,
}).partial({ id: true });
export type BankruptcyInput = z.infer<typeof BankruptcyInput>;
